// src/pages/dashboards/partner/PartnerOverview.jsx
// A partner's live view of their own network: how many people they have sent,
// how many converted, what that has earned, and what is coming up for renewal.
//
// Counts and money only. No subscriber names, emails or financial records.

import React, { useEffect, useState } from "react";
import { getOverview, getCodes, getConsultants, getCommission } from "../../../api/partner";
import { Card, Input, Button, Badge, Loading, EmptyState, Field } from "../../../components/ui";
import { DonutChart, BarChart, HBarChart } from "../../../components/charts";
import { money } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const pct = (r) => (r == null ? "\u2014" : `${Math.round(Number(r) * 1000) / 10}%`);
const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-ZA", { month: "short" });
};
const TIER_LABEL = { starter: "Starter", pro: "Pro" };
const TIER_COLOR = { starter: "#22D3B2", pro: "#00B2FF" };
const TIER_PRICE = { starter: 499, pro: 950 };

// ---------------------------------------------------------------- stat card

function Stat({ label, value, hint, accent, sub }) {
  return (
    <Card className="px-5 py-4">
      <p className="text-[11px] uppercase tracking-wider text-tb-muted">{label}</p>
      <p className={`text-2xl font-bold mt-1.5 tabular-nums ${accent ? "text-tb-brand" : ""}`}>{value}</p>
      {sub && <p className="text-xs font-medium text-tb-brand mt-1">{sub}</p>}
      {hint && <p className="text-xs text-tb-muted mt-1">{hint}</p>}
    </Card>
  );
}

// ---------------------------------------------------------------- page

export default function PartnerOverview() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState(null);
  const [consultants, setConsultants] = useState(null);

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const loadReport = () => {
    setLoadingReport(true);
    getCommission({ from, to })
      .then(setReport)
      .catch((e) => { toast.error(e.message); setReport(null); })
      .finally(() => setLoadingReport(false));
  };

  useEffect(() => {
    getOverview().then(setData).catch((e) => { toast.error(e.message); setData(null); });
    getCodes().then(setCodes).catch(() => setCodes([]));
    getConsultants().then(setConsultants).catch(() => setConsultants([]));
    loadReport();
    // eslint-disable-next-line
  }, []);

  if (!data) return <Loading label="Loading your dashboard" />;

  const hasAnything = data.signups > 0;

  // projected commission on what is due to renew
  const renewalValue = (key) =>
    (data.renewals || []).reduce(
      (s, r) => s + (r[key] || 0) * (TIER_PRICE[r.tier] || 0) * data.partner.commission_rate,
      0
    );
  const due30 = (data.renewals || []).reduce((s, r) => s + (r.d30 || 0), 0);
  const due90 = (data.renewals || []).reduce((s, r) => s + (r.d90 || 0), 0);

  const trendSignups = (data.trend || []).map((t) => ({
    label: monthLabel(t.month),
    value: t.signups,
    color: "#00B2FF",
  }));

  const planData = (data.plans || []).map((p) => ({
    label: TIER_LABEL[p.tier] || p.tier,
    value: p.subscribers,
    color: TIER_COLOR[p.tier] || "#948A88",
  }));

  const activeCodes = (codes || []).filter((c) => c.signups > 0);

  return (
    <div>
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-display font-bold">{data.partner.name}</h1>
        <Badge tone="green">{pct(data.partner.commission_rate)} of list price</Badge>
      </div>
      <p className="text-sm text-tb-muted mb-6">
        Everyone who entered one of your codes when they signed up for taxpack.
      </p>

      {!hasAnything ? (
        <Card className="p-2">
          <EmptyState
            title="No signups yet"
            hint="Share your referral code. Anyone who enters it when they create their taxpack account appears here straight away."
          />
        </Card>
      ) : (
        <>
          {/* headline */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat
              label="Signups"
              value={data.signups}
              sub={data.signups_30d > 0 ? `+${data.signups_30d} in 30 days` : null}
              hint={data.practitioners > 0 ? `${data.practitioners} tax practitioners` : "all time"}
            />
            <Stat
              label="Subscribers"
              value={data.paying}
              hint={`${data.conversion}% of signups have paid`}
            />
            <Stat
              label="Revenue"
              value={money(data.revenue)}
              sub={data.revenue_30d > 0 ? `${money(data.revenue_30d)} in 30 days` : null}
              hint="all time"
            />
            <Stat
              label="Your commission"
              value={money(data.commission)}
              sub={data.commission_30d > 0 ? `${money(data.commission_30d)} in 30 days` : null}
              accent
            />
          </div>

          {/* renewals + pipeline - the forward looking numbers */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <Card className="p-5 md:col-span-2">
              <p className="text-[11px] uppercase tracking-wider text-tb-muted mb-4">Coming up for renewal</p>
              {due90 === 0 ? (
                <p className="text-sm text-tb-muted">
                  Nothing due in the next 90 days. Subscriptions run for a year from the date they were bought.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-tb-muted mb-1">Next 30 days</p>
                    <p className="text-2xl font-bold tabular-nums">{due30}</p>
                    <p className="text-sm text-tb-brand font-medium mt-1">
                      {money(renewalValue("d30"))} to you
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-tb-muted mb-1">Next 90 days</p>
                    <p className="text-2xl font-bold tabular-nums">{due90}</p>
                    <p className="text-sm text-tb-brand font-medium mt-1">
                      {money(renewalValue("d90"))} to you
                    </p>
                  </div>
                </div>
              )}
              <p className="text-[11px] text-tb-muted mt-4 leading-relaxed">
                Projected on current list prices, assuming every subscription renews.
              </p>
            </Card>

            <Card className="p-5">
              <p className="text-[11px] uppercase tracking-wider text-tb-muted mb-4">Yet to subscribe</p>
              <p className="text-3xl font-bold tabular-nums">{data.pipeline}</p>
              <p className="text-xs text-tb-muted mt-2 leading-relaxed">
                People who signed up with your code and are still on the free plan. Each one is
                worth up to {money(950 * data.partner.commission_rate)} a year to you if they upgrade.
              </p>
            </Card>
          </div>

          {/* trend + plan mix */}
          <div className="grid lg:grid-cols-3 gap-4 mb-8">
            <Card className="p-5 lg:col-span-2">
              <p className="text-[11px] uppercase tracking-wider text-tb-muted mb-5">
                Signups, last 12 months
              </p>
              <BarChart data={trendSignups} height={210} />
            </Card>

            <Card className="p-5">
              <p className="text-[11px] uppercase tracking-wider text-tb-muted mb-5">Plan mix</p>
              {planData.length === 0 ? (
                <p className="text-sm text-tb-muted">No paid subscriptions yet.</p>
              ) : (
                <div className="flex flex-col items-center">
                  <DonutChart data={planData} size={148} thickness={18} center={`${data.paying}`} />
                  <div className="w-full mt-5 space-y-2">
                    {planData.map((p) => (
                      <div key={p.label} className="flex items-center gap-2 text-sm">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: p.color }} />
                        <span className="text-tb-muted flex-1">{p.label}</span>
                        <span className="font-medium tabular-nums">{p.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* codes */}
          {activeCodes.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-display font-semibold mb-3">Your codes</h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                        <th className="px-4 py-3 font-medium">Code</th>
                        <th className="px-4 py-3 font-medium">Used by</th>
                        <th className="px-4 py-3 font-medium">Signups</th>
                        <th className="px-4 py-3 font-medium">Subscribers</th>
                        <th className="px-4 py-3 font-medium">Conversion</th>
                        <th className="px-4 py-3 font-medium text-right">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeCodes.map((c) => (
                        <tr key={c.id} className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0">
                          <td className="px-4 py-3 font-mono font-medium">
                            {c.code}
                            {!c.is_active && <span className="ml-2 text-xs text-tb-muted font-sans">closed</span>}
                          </td>
                          <td className="px-4 py-3 text-tb-muted">{c.consultant_name || c.label || "Company"}</td>
                          <td className="px-4 py-3 tabular-nums">{c.signups}</td>
                          <td className="px-4 py-3 tabular-nums">{c.paying}</td>
                          <td className="px-4 py-3 tabular-nums text-tb-muted">{c.conversion}%</td>
                          <td className="px-4 py-3 text-right font-semibold tabular-nums">{money(c.commission)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* consultants */}
          {consultants && consultants.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-display font-semibold mb-1">Your consultants</h2>
              <p className="text-sm text-tb-muted mb-3">
                Lifetime, so you can settle with each of them.
              </p>

              <div className="grid lg:grid-cols-3 gap-4">
                <Card className="p-5 lg:col-span-2">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                          <th className="py-2.5 font-medium">Consultant</th>
                          <th className="py-2.5 font-medium">Signups</th>
                          <th className="py-2.5 font-medium">Subscribers</th>
                          <th className="py-2.5 font-medium text-right">You earn</th>
                          <th className="py-2.5 font-medium text-right">Their share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consultants.map((c) => (
                          <tr key={c.id} className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0">
                            <td className="py-2.5 font-medium">
                              {c.name}
                              {!c.is_active && <span className="ml-2 text-xs text-tb-muted">inactive</span>}
                            </td>
                            <td className="py-2.5 tabular-nums">{c.signups}</td>
                            <td className="py-2.5 tabular-nums">{c.paying}</td>
                            <td className="py-2.5 text-right font-semibold tabular-nums">{money(c.commission)}</td>
                            <td className="py-2.5 text-right tabular-nums text-tb-muted">
                              {c.consultant_share == null ? "\u2014" : money(c.consultant_share)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card className="p-5">
                  <p className="text-[11px] uppercase tracking-wider text-tb-muted mb-4">Signups by consultant</p>
                  <HBarChart
                    data={consultants.map((c, i) => ({
                      label: c.name,
                      value: c.signups,
                      color: ["#00B2FF", "#22D3B2", "#9BEA55", "#A855F7", "#F5A623"][i % 5],
                    }))}
                  />
                </Card>
              </div>
            </div>
          )}

          {/* period */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <h2 className="text-lg font-display font-semibold mr-auto">Statement period</h2>
            <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
            <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
            <Button onClick={loadReport} disabled={loadingReport}>{loadingReport ? "Loading..." : "Run"}</Button>
          </div>

          {loadingReport ? (
            <Loading label="Calculating" />
          ) : !report || report.payments === 0 ? (
            <Card className="p-2">
              <EmptyState
                title="No payments in this period"
                hint="Commission is earned when someone who used your code pays for a taxpack subscription."
              />
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Stat label="Payments" value={report.payments} />
                <Stat label="Revenue" value={money(report.revenue)} />
                <Stat label="Commission" value={money(report.commission)} accent />
              </div>

              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                        <th className="px-4 py-3 font-medium">Source</th>
                        <th className="px-4 py-3 font-medium">Subscribers</th>
                        <th className="px-4 py-3 font-medium">Payments</th>
                        <th className="px-4 py-3 font-medium">Revenue</th>
                        <th className="px-4 py-3 font-medium text-right">You earn</th>
                        <th className="px-4 py-3 font-medium text-right">Their share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.consultants.map((c) => (
                        <tr key={c.consultant_id || "direct"} className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0">
                          <td className="px-4 py-3 font-medium">{c.consultant_name}</td>
                          <td className="px-4 py-3 tabular-nums">{c.users}</td>
                          <td className="px-4 py-3 tabular-nums">{c.payments}</td>
                          <td className="px-4 py-3 tabular-nums">{money(c.revenue)}</td>
                          <td className="px-4 py-3 text-right font-semibold tabular-nums">{money(c.commission)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-tb-muted">
                            {c.consultant_share == null ? "\u2014" : money(c.consultant_share)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          <p className="mt-4 text-xs text-tb-muted leading-relaxed max-w-3xl">
            Figures update as signups and payments happen, so this is always current. Attribution is
            fixed when someone registers with your code and never changes afterwards. Their share is
            what you have told us you pass on to each consultant &mdash; we settle with you, and you
            settle with them.
          </p>
        </>
      )}
    </div>
  );
}